import { useCallback } from "react";

import { checkEtaMessage } from "@/components/Dispatch/context/routeFormat";
import { recordSpan } from "@/services/telemetry";
import { store } from "@/store/store";
import type { RouteStopSummary } from "@/types/driverRoute";
import { sendComposerDraft } from "./composerSend";

/**
 * Sends a driver the Check ETA message for their next stop, or for no known stop, through the same publish the
 * Dispatch composer uses.
 *
 * @param workspaceId - The workspace the driver's channel is in.
 * @param feedId - The driver's channel; null when the driver has none, and there is nothing to send to.
 * @returns The send for a stop, which rejects when the publish fails; null when the driver has no channel.
 */
export function useCheckEta(
  workspaceId: string,
  feedId: string | null,
): ((stop: RouteStopSummary | null) => Promise<void>) | null {
  const send = useCallback(
    async (stop: RouteStopSummary | null) => {
      if (!feedId) return;
      const startedAt = new Date();
      const attributes = { checkEta: { stopRole: stop?.role ?? null, stopRisk: stop?.risk ?? null } };
      try {
        await sendComposerDraft({
          workspaceId,
          feedId,
          draft: { text: checkEtaMessage(stop), attachment: null },
          isSilent: store.get("feed", feedId)?.isSilent,
        });
        recordSpan("dispatch_check_eta.send", startedAt, attributes);
      } catch (error) {
        recordSpan("dispatch_check_eta.send_failed", startedAt, attributes, "reliability", { isError: true });
        throw error;
      }
    },
    [workspaceId, feedId],
  );
  return feedId ? send : null;
}
